import { useEffect, useState } from 'react'

export type Theme = 'light' | 'dark'

const STORAGE_KEY = 'reydelgreen:theme'

/**
 * Escolha manual, se houver; senão o que o sistema pede.
 *
 * Exportada porque o tema precisa valer antes do app: login e convite ficam
 * fora do layout autenticado e nasciam sempre claros, mesmo com o aparelho no
 * escuro.
 */
export function preferredTheme(): Theme {
  const saved = localStorage.getItem(STORAGE_KEY)
  if (saved === 'light' || saved === 'dark') return saved
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

export function applyTheme(theme: Theme) {
  document.documentElement.classList.toggle('dark', theme === 'dark')
  pintarBarraDeStatus()
}

/**
 * A barra de status do aparelho herda a cor da barra do app.
 *
 * Lê `--surface` já resolvido em vez de repetir o hexa aqui: se a paleta
 * mudar, relógio e cabeçalho continuam da mesma cor sozinhos.
 *
 * As duas metas por `prefers-color-scheme` no HTML são só o estado inicial,
 * antes do JS subir. A partir daqui manda a escolha manual do tema — que
 * media query nenhuma enxerga, e era por isso que no tema claro forçado a
 * barra continuava escura.
 */
function pintarBarraDeStatus() {
  const surface = getComputedStyle(document.documentElement).getPropertyValue('--surface').trim()
  // No primeiro passo o CSS pode não ter chegado ainda; o efeito de `useTheme`
  // chama de novo depois da montagem, aí já resolve.
  if (!surface) return

  for (const meta of document.querySelectorAll('meta[name="theme-color"]')) {
    meta.setAttribute('content', surface)
    meta.removeAttribute('media')
  }
}

/**
 * Tema do sistema por padrão, com escolha manual persistida. A classe `.dark`
 * no `<html>` troca a paleta inteira de uma vez — os tokens resolvem em
 * runtime, então nenhum componente precisa de `dark:` espalhado.
 */
export function useTheme(): [Theme, () => void] {
  const [theme, setTheme] = useState<Theme>(() => {
    if (typeof window === 'undefined') return 'light'
    return preferredTheme()
  })

  useEffect(() => {
    applyTheme(theme)
  }, [theme])

  const toggle = () => {
    setTheme((current) => {
      const next = current === 'dark' ? 'light' : 'dark'
      localStorage.setItem(STORAGE_KEY, next)
      return next
    })
  }

  return [theme, toggle]
}
